import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import AdUnit from './AdUnit.jsx'
import DocumentMeta from './DocumentMeta.jsx'
import { supabase } from '../lib/supabase'
import { MAX_YEAR } from './constants.js'
import { buildRecommendations, BUCKETS } from './lib/aiAdvisor.js'
import { trackEvent } from './lib/analytics'
import { reportError } from './lib/reportError'
import {
  SparklesIcon,
  CalculatorIcon,
  AcademicCapIcon,
  MapPinIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline'

const TARGET_YEAR = MAX_YEAR + 1 // 2027
const PER_BUCKET = 10

const ANALYZE_STEPS = [
  `Ανάλυση ιστορικών βάσεων 2018–${MAX_YEAR}…`,
  'Αξιολόγηση τάσης ανά έτος και τμήμα…',
  `Πρόβλεψη βάσεων ${TARGET_YEAR}…`,
  'Σύγκριση με τα μόριά σου…',
  'Εντοπισμός τμημάτων που σου ταιριάζουν…',
  'Κατάταξη & βαθμολόγηση προτάσεων…',
]

// Timing of the "AI is analyzing" animation. The real work is near-instant, so
// we deliberately let the step-by-step animation play out to make the analysis
// feel substantial. STEP_MS = time each step is highlighted; MIN_ANALYZE_MS =
// the floor for the whole animation even when the data fetch returns early.
const STEP_MS = 620
const MIN_ANALYZE_MS = ANALYZE_STEPS.length * STEP_MS + 700 // ~4.4s

const BUCKET_STYLES = {
  safe: {
    ring: 'text-emerald-600',
    bar: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
  },
  match: {
    ring: 'text-gray-900',
    bar: 'bg-gray-900',
    chip: 'bg-gray-100 text-gray-800 border-gray-200',
    dot: 'bg-gray-900',
  },
  reach: {
    ring: 'text-amber-600',
    bar: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
  },
}

function fmt(n) {
  return Number(n).toLocaleString('el-GR')
}

const BUCKET_KEYS = ['safe', 'match', 'reach']

// Unique cities present across all recommendation buckets, with a count each,
// sorted by how many schools they hold (most first) so the most useful filters
// surface at the front of the chip row; ties break alphabetically (Greek).
function citiesFromGrouped(grouped) {
  const counts = new Map()
  for (const key of BUCKET_KEYS) {
    for (const item of grouped[key] || []) {
      const c = item.department.city
      if (!c) continue
      counts.set(c, (counts.get(c) || 0) + 1)
    }
  }
  return [...counts.entries()]
    .map(([city, count]) => ({ city, count }))
    .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city, 'el'))
}

// How many city chips to show before the "more" toggle.
const CITY_CHIPS_VISIBLE = 10

// Keep only recommendations in the chosen city (empty city = keep all).
function filterGroupedByCity(grouped, city) {
  if (!city) return grouped
  const out = { safe: [], match: [], reach: [] }
  for (const key of BUCKET_KEYS) {
    out[key] = (grouped[key] || []).filter((i) => i.department.city === city)
  }
  return out
}

async function fetchFieldScores(fieldId) {
  const pageSize = 1000
  let from = 0
  let all = []
  // Loop past Supabase's 1000-row cap so we get every year for every dept.
  for (;;) {
    const { data, error } = await supabase
      .from('scores')
      .select(
        `year, score,
         departments!inner(id, name, university, city, slug),
         score_fields!inner(field_id)`
      )
      .eq('score_fields.field_id', fieldId)
      .order('score', { ascending: false })
      .range(from, from + pageSize - 1)
    if (error) throw error
    all = all.concat(data || [])
    if (!data || data.length < pageSize) break
    from += pageSize
  }
  return all.map((r) => ({ year: r.year, score: r.score, department: r.departments }))
}

function MatchBar({ value, colorClass }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
      <div
        className={`h-full rounded-full ${colorClass} transition-[width] duration-700 motion-reduce:transition-none`}
        style={{ width: `${value}%` }}
      />
    </div>
  )
}

function CityChip({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition active:scale-[0.97] motion-reduce:active:scale-100 cursor-pointer ${
        active
          ? 'border-gray-900 bg-gray-900 text-white'
          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      {label}
      <span
        className={`tabular-nums text-xs ${active ? 'text-white/70' : 'text-gray-400'}`}
      >
        {count}
      </span>
    </button>
  )
}

function RecoCard({ item }) {
  const s = BUCKET_STYLES[item.bucket]
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 hover:border-gray-300 hover:shadow-sm transition motion-reduce:transition-none">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 leading-snug">
            {item.department.name}
          </h4>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
            <AcademicCapIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{item.department.university}</span>
          </p>
          {item.department.city ? (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
              <MapPinIcon className="h-3.5 w-3.5 shrink-0" />
              {item.department.city}
            </p>
          ) : null}
        </div>
        <div className={`text-right shrink-0 ${s.ring}`}>
          <div className="text-2xl font-bold tabular-nums leading-none">{item.match}%</div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400">match</div>
        </div>
      </div>

      <div className="mt-4">
        <MatchBar value={item.match} colorClass={s.bar} />
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-xs text-gray-500">Εκτιμ. βάση {TARGET_YEAR}</dt>
          <dd className="font-semibold text-gray-900 tabular-nums">{fmt(item.predicted)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Βάση {item.lastYear}</dt>
          <dd className="font-semibold text-gray-700 tabular-nums">
            {item.lastScore != null ? fmt(item.lastScore) : '—'}
          </dd>
        </div>
      </dl>

      <Link
        to={item.department.slug ? `/tmima/${item.department.slug}` : `/vaseis/${MAX_YEAR}`}
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-gray-600"
      >
        Δες λεπτομέρειες
        <ArrowRightIcon className="h-4 w-4" />
      </Link>
    </article>
  )
}

function AiAdvisorPage() {
  const [fields, setFields] = useState([])
  const [fieldId, setFieldId] = useState('')
  const [score, setScore] = useState('')
  const [status, setStatus] = useState('idle') // idle | analyzing | done | error
  const [stepIdx, setStepIdx] = useState(0)
  const [grouped, setGrouped] = useState(null)
  const [totalCount, setTotalCount] = useState(0)
  const [errorMsg, setErrorMsg] = useState('')
  const [expanded, setExpanded] = useState({})
  const [cityFilter, setCityFilter] = useState('')
  const [showAllCities, setShowAllCities] = useState(false)
  const stepTimer = useRef(null)

  useEffect(() => {
    let alive = true
    supabase
      .from('fields')
      .select('id, number, name')
      .order('number')
      .then(({ data, error }) => {
        if (error) {
          reportError(error, { where: 'AiAdvisor.fetchFields' })
          return
        }
        if (alive) setFields(data || [])
      })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    return () => clearInterval(stepTimer.current)
  }, [])

  const userScore = Number(score)
  const scoreValid = Number.isFinite(userScore) && userScore >= 1000 && userScore <= 24000

  async function analyze(e) {
    e?.preventDefault()
    setErrorMsg('')
    if (!fieldId) {
      setErrorMsg('Διάλεξε επιστημονικό πεδίο.')
      return
    }
    if (!scoreValid) {
      setErrorMsg('Βάλε ρεαλιστικό αριθμό μορίων (1.000 – 24.000).')
      return
    }

    trackEvent('ai_advisor_analyze', { field: fieldId, score: userScore })

    setStatus('analyzing')
    setGrouped(null)
    setStepIdx(0)
    clearInterval(stepTimer.current)
    stepTimer.current = setInterval(() => {
      setStepIdx((i) => Math.min(i + 1, ANALYZE_STEPS.length - 1))
    }, STEP_MS)

    const startedAt = Date.now()
    try {
      const rows = await fetchFieldScores(fieldId)
      const result = buildRecommendations(rows, userScore, TARGET_YEAR)
      const total = result.safe.length + result.match.length + result.reach.length

      // Let the "AI is thinking" animation breathe even on a fast fetch.
      const elapsed = Date.now() - startedAt
      if (elapsed < MIN_ANALYZE_MS) {
        await new Promise((r) => setTimeout(r, MIN_ANALYZE_MS - elapsed))
      }

      clearInterval(stepTimer.current)
      setGrouped(result)
      setTotalCount(total)
      setExpanded({})
      setCityFilter('')
      setShowAllCities(false)
      setStatus('done')
      trackEvent('ai_advisor_results', { field: fieldId, score: userScore, total })
    } catch (err) {
      clearInterval(stepTimer.current)
      reportError(err, { where: 'AiAdvisor.analyze', field: fieldId })
      setErrorMsg('Κάτι πήγε στραβά κατά την ανάλυση. Δοκίμασε ξανά.')
      setStatus('error')
    }
  }

  const cityOptions = grouped ? citiesFromGrouped(grouped) : []
  const view = grouped ? filterGroupedByCity(grouped, cityFilter) : null
  const viewTotal = view ? view.safe.length + view.match.length + view.reach.length : 0

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <DocumentMeta
        title={`AI Σύμβουλος Σχολών ${TARGET_YEAR} | Πανελλήνιες+`}
        description={`Βάλε τα μόριά σου και δες με «AI» ποιες σχολές σου ταιριάζουν, με εκτίμηση βάσεων ${TARGET_YEAR} από τα ιστορικά δεδομένα 2018–${MAX_YEAR}.`}
        canonical="https://www.panellhniesplus.gr/ai-symvoulos"
      />
      <Header />

      {/* HERO */}
      <section className="border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12 sm:py-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm">
            <SparklesIcon className="h-4 w-4" />
            AI Σύμβουλος
            <span className="rounded-full bg-gray-900 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              BETA
            </span>
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">
            Ποιες σχολές σου ταιριάζουν;
          </h1>
          <p className="mt-3 max-w-2xl text-base sm:text-lg text-gray-600 leading-relaxed">
            Βάλε τα μόριά σου και το επιστημονικό σου πεδίο. Ο AI Σύμβουλος αναλύει τις
            βάσεις 2018–{MAX_YEAR}, εκτιμά τη βάση κάθε τμήματος για το {TARGET_YEAR} και
            σου προτείνει ασφαλείς επιλογές, καλές πιθανότητες και φιλόδοξους στόχους.
          </p>

          {/* INPUT CARD */}
          <form
            onSubmit={analyze}
            className="mt-8 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm"
          >
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 sm:items-end">
              <div>
                <label htmlFor="ai-score" className="block text-sm font-medium text-gray-900">
                  Τα μόριά σου
                </label>
                <input
                  id="ai-score"
                  type="number"
                  inputMode="numeric"
                  min={1000}
                  max={24000}
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  placeholder="π.χ. 18500"
                  className="mt-1.5 w-full rounded-xl border border-gray-300 px-4 py-3 text-base tabular-nums outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                />
              </div>
              <div>
                <label htmlFor="ai-field" className="block text-sm font-medium text-gray-900">
                  Επιστημονικό πεδίο
                </label>
                <select
                  id="ai-field"
                  value={fieldId}
                  onChange={(e) => setFieldId(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                >
                  <option value="">Διάλεξε πεδίο…</option>
                  {fields.map((f) => (
                    <option key={f.id} value={f.id}>
                      Πεδίο {f.number} — {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={status === 'analyzing'}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-6 py-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60 transition h-[50px]"
              >
                <SparklesIcon className="h-4 w-4" />
                {status === 'analyzing' ? 'Ανάλυση…' : 'Ανάλυσε'}
              </button>
            </div>

            {errorMsg ? (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {errorMsg}
              </p>
            ) : null}

            <p className="mt-3 text-xs text-gray-500">
              Δεν ξέρεις τα μόριά σου;{' '}
              <Link to="/ypologismos-morion" className="inline-flex items-center gap-1 font-medium text-gray-900 underline underline-offset-2">
                <CalculatorIcon className="h-3.5 w-3.5" />
                Υπολόγισέ τα εδώ
              </Link>
            </p>
          </form>
        </div>
      </section>

      {/* ANALYZING ANIMATION */}
      {status === 'analyzing' ? (
        <section className="mx-auto max-w-5xl px-4 sm:px-6 py-14" aria-live="polite" aria-busy="true">
          <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gray-900 text-white">
                <SparklesIcon className="h-5 w-5 animate-pulse motion-reduce:animate-none" />
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-900">Ο AI Σύμβουλος αναλύει…</p>
                <p className="text-xs text-gray-500">Επεξεργασία ιστορικών δεδομένων</p>
              </div>
            </div>
            <ul className="mt-5 space-y-2.5">
              {ANALYZE_STEPS.map((label, i) => {
                const state = i < stepIdx ? 'done' : i === stepIdx ? 'active' : 'todo'
                return (
                  <li key={label} className="flex items-center gap-3 text-sm">
                    <span
                      className={
                        state === 'done'
                          ? 'flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white text-[11px]'
                          : state === 'active'
                          ? 'flex h-5 w-5 items-center justify-center rounded-full border-2 border-gray-900 border-t-transparent animate-spin motion-reduce:animate-none'
                          : 'flex h-5 w-5 items-center justify-center rounded-full border border-gray-200'
                      }
                    >
                      {state === 'done' ? '✓' : ''}
                    </span>
                    <span className={state === 'todo' ? 'text-gray-400' : 'text-gray-800'}>
                      {label}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        </section>
      ) : null}

      {/* RESULTS */}
      {status === 'done' && grouped ? (
        <section className="mx-auto max-w-5xl px-4 sm:px-6 py-12 sm:py-16" aria-live="polite">
          {totalCount === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-8 text-center">
              <p className="text-base font-medium text-gray-900">
                Δεν βρέθηκαν τμήματα κοντά στα μόριά σου σε αυτό το πεδίο.
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Δοκίμασε διαφορετικό πεδίο ή έλεγξε ξανά τα μόριά σου.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-gray-300" />
                <span className="text-xs font-medium uppercase tracking-[0.2em] text-gray-500">
                  Προτάσεις AI
                </span>
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight">
                Βρήκα {totalCount} σχολές για τα {fmt(userScore)} μόριά σου
              </h2>
              <p className="mt-2 text-sm text-gray-500 max-w-2xl">
                Οι εκτιμήσεις βάσεων {TARGET_YEAR} είναι ενδεικτικές, βασισμένες στην τάση
                των ιστορικών δεδομένων — δεν αποτελούν επίσημη πρόβλεψη.
              </p>

              {/* CITY FILTER — selectable chips */}
              {cityOptions.length > 1 ? (
                <div className="mt-7">
                  <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-gray-500">
                    <MapPinIcon className="h-4 w-4" />
                    Φιλτράρισε ανά πόλη
                  </div>
                  <div
                    role="group"
                    aria-label="Φίλτρο πόλης"
                    className="mt-3 flex flex-wrap gap-2"
                  >
                    <CityChip
                      label="Όλες"
                      count={totalCount}
                      active={cityFilter === ''}
                      onClick={() => setCityFilter('')}
                    />
                    {(showAllCities ? cityOptions : cityOptions.slice(0, CITY_CHIPS_VISIBLE)).map(
                      ({ city, count }) => (
                        <CityChip
                          key={city}
                          label={city}
                          count={count}
                          active={cityFilter === city}
                          onClick={() => setCityFilter(cityFilter === city ? '' : city)}
                        />
                      )
                    )}
                    {cityOptions.length > CITY_CHIPS_VISIBLE ? (
                      <button
                        type="button"
                        onClick={() => setShowAllCities((v) => !v)}
                        className="inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-medium text-gray-600 underline underline-offset-2 hover:text-gray-900 cursor-pointer"
                      >
                        {showAllCities
                          ? 'Λιγότερες'
                          : `+${cityOptions.length - CITY_CHIPS_VISIBLE} ακόμη`}
                      </button>
                    ) : null}
                  </div>
                  <p className="mt-3 text-sm text-gray-500" aria-live="polite">
                    {cityFilter ? (
                      <>
                        <span className="font-semibold text-gray-900 tabular-nums">{viewTotal}</span>{' '}
                        {viewTotal === 1 ? 'σχολή' : 'σχολές'} στην πόλη{' '}
                        <span className="font-medium text-gray-900">{cityFilter}</span>
                      </>
                    ) : (
                      `${cityOptions.length} πόλεις διαθέσιμες`
                    )}
                  </p>
                </div>
              ) : null}

              <div className="mt-10 space-y-12">
                {BUCKET_KEYS.map((key) => {
                  const list = view[key]
                  if (!list || list.length === 0) return null
                  const isOpen = expanded[key]
                  const shown = isOpen ? list : list.slice(0, PER_BUCKET)
                  const meta = BUCKETS[key]
                  const st = BUCKET_STYLES[key]
                  return (
                    <div key={key}>
                      <div className="flex items-center gap-2.5">
                        <span className={`h-2.5 w-2.5 rounded-full ${st.dot}`} />
                        <h3 className="text-lg font-semibold text-gray-900">{meta.label}</h3>
                        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${st.chip}`}>
                          {list.length}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">{meta.hint}</p>

                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {shown.map((item) => (
                          <RecoCard key={item.department.id} item={item} />
                        ))}
                      </div>

                      {list.length > PER_BUCKET ? (
                        <button
                          onClick={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}
                          className="mt-4 text-sm font-medium text-gray-900 underline underline-offset-2 hover:text-gray-600"
                        >
                          {isOpen ? 'Δες λιγότερα' : `Δες και τις υπόλοιπες ${list.length - PER_BUCKET}`}
                        </button>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </section>
      ) : null}

      {/* In-content ad — after the recommendations, before the explainer */}
      {status === 'done' && totalCount > 0 ? (
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <AdUnit />
        </div>
      ) : null}

      {/* SEO / EXPLAINER CONTENT (always in HTML) */}
      <section className="bg-gray-50 border-t border-gray-100">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            Πώς λειτουργεί ο AI Σύμβουλος
          </h2>
          <div className="mt-6 space-y-6 text-sm sm:text-base text-gray-700 leading-relaxed">
            <p>
              Ο AI Σύμβουλος Σχολών αξιοποιεί τις πραγματικές βάσεις εισαγωγής των Πανελληνίων
              από το 2018 έως το {MAX_YEAR}. Για κάθε τμήμα του πεδίου που επιλέγεις, εντοπίζει
              την τάση των βάσεων διαχρονικά και εκτιμά μια πιθανή βάση για το {TARGET_YEAR}.
            </p>
            <p>
              Στη συνέχεια συγκρίνει τα μόριά σου με την εκτιμώμενη βάση κάθε σχολής και
              κατατάσσει τις προτάσεις σε τρεις κατηγορίες:{' '}
              <strong>ασφαλείς επιλογές</strong> (καλύπτεις άνετα τη βάση),{' '}
              <strong>καλές πιθανότητες</strong> (είσαι κοντά ή λίγο πάνω) και{' '}
              <strong>φιλόδοξους στόχους</strong> (οριακά κάτω από τη βάση). Έτσι χτίζεις μια
              ισορροπημένη λίστα σχολών, χωρίς να «καίς» επιλογές.
            </p>
            <p>
              Οι εκτιμήσεις είναι ενδεικτικές και δεν αντικαθιστούν την επίσημη διαδικασία του
              μηχανογραφικού. Χρησιμοποίησέ τες ως οδηγό για να στοχεύσεις σωστά και συνδύασέ
              τες με τις{' '}
              <Link to={`/vaseis/${MAX_YEAR}`} className="text-gray-900 underline underline-offset-2">
                αναλυτικές βάσεις
              </Link>{' '}
              και τον{' '}
              <Link to="/ypologismos-morion" className="text-gray-900 underline underline-offset-2">
                υπολογισμό μορίων
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default AiAdvisorPage
